// Utilities specific to products in inventory-management domain

// Sort product attributes by an optional order preference
// `order` is an array of identifiers in priority order.
// Supports both productAttributeId and customFieldId.
// Attributes not present in `order` are appended alphabetically by field name
export function sortProductAttributesByOrder<
  T extends {
    id: string; // productAttributeId
    customField: { id: string; fieldName: string };
  },
>(attributes: T[], order?: string[] | null): T[] {
  if (!attributes || attributes.length === 0) return attributes;
  if (!order || order.length === 0) {
    return [...attributes].sort((a, b) =>
      a.customField.fieldName.localeCompare(b.customField.fieldName),
    );
  }

  const position: Record<string, number> = {};
  order.forEach((identifier, idx) => {
    position[identifier] = idx;
  });

  return [...attributes].sort((a, b) => {
    // Prefer explicit productAttributeId matches over customFieldId
    const aPosAttr = position[a.id];
    const bPosAttr = position[b.id];
    const aPosField = position[a.customField.id];
    const bPosField = position[b.customField.id];

    const aHas = aPosAttr ?? aPosField;
    const bHas = bPosAttr ?? bPosField;

    const aIn = aHas !== undefined;
    const bIn = bHas !== undefined;

    if (aIn && bIn) return (aHas as number) - (bHas as number);
    if (aIn) return -1;
    if (bIn) return 1;
    // Neither specified: fall back to name
    return a.customField.fieldName.localeCompare(b.customField.fieldName);
  });
}
